import React from 'react';
import { X, CheckCircle, XCircle } from 'lucide-react';
import { useTranslation } from '../../../../../contexts/I18nProvider';
import { ConsolidationExecuteResponse } from '../../../../../types/consolidation';

interface ExecuteResultPanelProps {
    result: ConsolidationExecuteResponse;
    onDismiss: () => void;
}

/**
 * Resultatpanel etter utførelse: grønn (alt OK), gul (delvis) eller
 * rød (alt feilet) boks, med per-linje-tabell ved delvis/full feil.
 */
export const ExecuteResultPanel: React.FC<ExecuteResultPanelProps> = ({ result, onDismiss }) => {
    const { t } = useTranslation();

    const boxClass = result.failedCount === 0
        ? 'bg-green-50 border-green-200 text-green-800'
        : result.successCount === 0
            ? 'bg-red-50 border-red-200 text-red-800'
            : 'bg-amber-50 border-amber-200 text-amber-800';

    const title = result.failedCount === 0
        ? t('manager.modules.consolidation.result.allOk', { '0': result.successCount }, `Alle ${result.successCount} linjer utført`)
        : result.successCount === 0
            ? t('manager.modules.consolidation.result.allFailed', { '0': result.failedCount }, `Utførelse feilet: ${result.failedCount} linjer feilet`)
            : t('manager.modules.consolidation.result.partial', { '0': result.successCount, '1': result.failedCount }, `Delvis fullført: ${result.successCount} lykkes, ${result.failedCount} feilet`);

    return (
        <div className={`border rounded-lg p-3 ${boxClass}`}>
            <div className="flex items-start justify-between">
                <div className="font-semibold text-sm">{title}</div>
                <button
                    onClick={onDismiss}
                    className="p-0.5 rounded hover:bg-black hover:bg-opacity-5"
                    aria-label={t('common.buttons.close', {}, 'Lukk')}
                >
                    <X className="w-4 h-4" />
                </button>
            </div>

            {result.stcOrderNo != null && (
                <div className="text-xs mt-0.5">
                    {t('manager.modules.consolidation.result.orderCreated', { '0': result.stcOrderNo }, `Utleggsordre #${result.stcOrderNo} opprettet — utføres i Plassering`)}
                </div>
            )}
            {result.moveProcessId != null && (
                <div className="text-xs mt-0.5">
                    {t('manager.modules.consolidation.result.planCreated', { '0': result.moveProcessId }, `Lagt i flytteplan #${result.moveProcessId}`)}
                </div>
            )}
            {result.errorMessage && (
                <div className="text-xs mt-0.5">{result.errorMessage}</div>
            )}

            {result.failedCount > 0 && result.results.length > 0 && (
                <div className="mt-2 border border-gray-200 rounded-lg overflow-hidden bg-white">
                    <table className="w-full text-xs">
                        <thead className="bg-gray-50 text-gray-600">
                            <tr>
                                <th className="px-2 py-1.5 text-left">{t('manager.modules.consolidation.table.fromBin', {}, 'Fra lokasjon')}</th>
                                <th className="px-2 py-1.5 text-left">{t('manager.modules.consolidation.table.target', {}, 'Mål')}</th>
                                <th className="px-2 py-1.5 text-left">{t('warehouse.general.item', {}, 'Vare')}</th>
                                <th className="px-2 py-1.5 text-right">{t('manager.modules.consolidation.table.quantity', {}, 'Antall')}</th>
                                <th className="px-2 py-1.5 text-left">{t('common.fields.status', {}, 'Status')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {result.results.map((r, i) => (
                                <tr key={i} className="border-t border-gray-100 text-gray-800">
                                    <td className="px-2 py-1.5 font-mono">{r.fromBinCode}</td>
                                    <td className="px-2 py-1.5 font-mono">{r.toBinCode}</td>
                                    <td className="px-2 py-1.5 font-mono">{r.itemNo}</td>
                                    <td className="px-2 py-1.5 text-right">{r.quantity}</td>
                                    <td className="px-2 py-1.5">
                                        {r.success ? (
                                            <span className="inline-flex items-center text-green-700">
                                                <CheckCircle className="w-3.5 h-3.5 mr-1" />
                                                {t('manager.modules.consolidation.result.ok', {}, 'OK')}
                                            </span>
                                        ) : (
                                            <span className="inline-flex items-center text-red-700" title={r.errorMessage}>
                                                <XCircle className="w-3.5 h-3.5 mr-1" />
                                                {r.errorMessage || t('manager.modules.consolidation.result.failed', {}, 'Feilet')}
                                            </span>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};
